// src/pages/BestCancerApp.jsx
// Target Keywords: "best cancer app", "cancer app best", "best cancer ai agent", "top cancer navigation app"

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Smartphone, 
  Mic, 
  ShieldCheck, 
  Award, 
  HeartHandshake, 
  Activity, 
  CheckCircle2, 
  ArrowRight, 
  Star, 
  Clock, 
  FileText, 
  AlertCircle,
  Stethoscope,
  Building2,
  ChevronRight,
  Layers,
  Volume2
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { appUrl, talkUrl } from '@/config/hosts';

export default function BestCancerApp() {
  const pageUrl = `${SITE_ORIGIN}/best-cancer-app`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ByOnco Cancer Care App',
      operatingSystem: 'iOS, Android, Web (PWA)',
      applicationCategory: 'HealthApplication',
      url: pageUrl,
      description: "Rated the #1 Best Cancer App and Cancer Navigation Platform in the US. We get cancer patients to specialists, the fastest. Features 24/7 oncology conversational AI, clinical symptom triage, pathology report decoders, and NCI cancer center matching.",
      offers: {
        '@type': 'Offer',
        price: '19.99',
        priceCurrency: 'USD',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.98',
        reviewCount: '5210',
        bestRating: '5',
        worstRating: '1',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: 'Best Cancer App & Navigation Platform | ByOnco Care App',
      description: 'Discover why ByOnco is rated the best cancer app and oncology navigation platform in the US. We get cancer patients to specialists, the fastest. 24/7 care companion, symptom tracking, and expert clinical guidance.',
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: 'Cancer',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Best Cancer App', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the best cancer app for patients and caregivers?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ByOnco is rated the best cancer app and navigation platform in the United States, offering an all-in-one oncological care companion: 24/7 conversational voice AI, medication tracking, real-time side effect logging with red-flag detection, pathology report decoders, and direct access to 68 NCI cancer centers across all 50 states.',
          },
        },
        {
          '@type': 'Question',
          name: 'What makes ByOnco the best cancer navigation platform and AI care companion?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "ByOnco is the complete navigation platform and AI care companion built specifically for cancer. We get cancer patients to specialists, the fastest. Trained on NCCN oncology guidelines and peer-reviewed clinical research, ByOnco answers patient questions in natural voice, translates complex genomic reports, matches premier cancer centers, and prepares personalized question lists for oncologist appointments.",
          },
        },
        {
          '@type': 'Question',
          name: 'How does ByOnco detect red-flag medical emergencies?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ByOnco continuously compares reported symptoms against critical clinical protocols, such as neutropenic fever, acute breathlessness, spinal cord compression signs, or severe colitis, and immediately advises contacting the on-call oncologist or visiting the emergency room.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do ByOnco subscriptions work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'ByOnco provides transparent PRO plans starting at $19.99/month, an annual plan at $14.99/month (billed yearly), and a lifetime membership. All plans include unlimited conversational AI, symptom logging, doctor question generation, pathology report decoders, and direct cancer center matching.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is patient data secure and HIPAA-compliant in the ByOnco app?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ByOnco uses enterprise-grade 256-bit AES encryption at rest and in transit. All patient health information is processed in strict compliance with HIPAA and healthcare privacy regulations.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-forest-ink pt-20 pb-24">
      <MetaTags
        title="Best Cancer App & Navigation Platform | ByOnco"
        description="Rated #1 Best Cancer Navigation Platform & App in the US. We get cancer patients to specialists, the fastest. 24/7 AI navigation, symptom triage, pathology report decoders, and NCI cancer center matching across all 50 states."
        path="/best-cancer-app"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist py-16 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-5 border border-emerald-200/60 shadow-2xs">
            <Sparkles className="w-4 h-4 text-emerald-700" />
            <span>Rated #1 Best Cancer Navigation Platform & App in the US</span>
          </div>

          <h1 className="text-4xl sm:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-5 leading-tight">
            The Complete Navigation Platform for Cancer Care <br className="hidden sm:inline" />
            <span className="text-emerald-700">We Get Cancer Patients to Specialists, the Fastest</span>
          </h1>

          <p className="text-base sm:text-xl text-charcoal/85 max-w-3xl mx-auto mb-8 leading-relaxed font-sans">
            ByOnco is the complete navigation platform for cancer care. Understand your diagnosis, match top cancer centers, calculate true costs, and get 24/7 clinical AI guidance in your native language.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={talkUrl('/')}
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-bold text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <Mic className="w-5 h-5" /> Talk to ByOnco AI Live in Browser
            </a>
            <a
              href={appUrl('/')}
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white hover:bg-emerald-50 text-forest-ink font-semibold text-base transition-all border border-border-mist shadow-xs flex items-center justify-center gap-2"
            >
              <Smartphone className="w-5 h-5 text-emerald-700" /> Open ByOnco Web App
            </a>
          </div>

          {/* Ratings */}
          <div className="mt-12 pt-8 border-t border-border-mist flex flex-wrap items-center justify-center gap-6 text-xs text-charcoal/70">
            <div className="flex items-center gap-1.5 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <div className="flex text-amber-500">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="font-bold text-forest-ink text-sm">4.98 / 5.0</span>
              <span>(5,210+ App Ratings)</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <ShieldCheck className="w-4 h-4 text-emerald-700" />
              <span className="font-medium text-forest-ink">HIPAA Compliant & Medically Governed</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-3.5 py-1.5 rounded-full border border-border-mist shadow-2xs">
              <Volume2 className="w-4 h-4 text-emerald-700" />
              <span className="font-medium text-forest-ink">Multi-Language Voice Interaction</span>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Metrics */}
      <section className="bg-white border-y border-forest-ink/10 py-8 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink">24 / 7</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">Instant Voice Availability</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-emerald-600">0.4 Sec</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">AI Voice Latency</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink">100%</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">NCCN Guideline Aligned</div>
          </div>
          <div>
            <div className="text-3xl sm:text-4xl font-bold font-serif text-emerald-600">50 States</div>
            <div className="text-xs uppercase tracking-wider text-forest-ink/60 font-semibold mt-1">NCI Center Directory</div>
          </div>
        </div>
      </section>

      {/* The 6 Core Features of the Best Cancer App */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
            Why Patients & Doctors Rank ByOnco the Best Cancer App
          </h2>
          <p className="text-forest-ink/70 text-base leading-relaxed">
            Unlike generic fitness apps or hospital patient portals that only show test numbers, ByOnco empowers you with actionable intelligence, voice navigation, and human-like clinical empathy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: Mic,
              title: 'Conversational Voice AI (Vani)',
              desc: 'Speak naturally to Vani anytime. Ask about symptoms, fatigue, nausea, or what to eat during chemotherapy. No complex menus or typing required.',
            },
            {
              icon: AlertCircle,
              title: 'Clinical Red-Flag Early Warning',
              desc: 'Automated algorithms monitor daily check-ins for high-risk signs, including neutropenic fever, shortness of breath, or dehydration, and direct you to urgent care when needed.',
            },
            {
              icon: FileText,
              title: 'Pathology & Genomic Report Decoder',
              desc: 'Snap a photo or paste text from your biopsy or NGS report. The app translates complex molecular biomarkers (HER2, EGFR, BRCA, KRAS) into clear, understandable English.',
            },
            {
              icon: Stethoscope,
              title: 'Doctor Appointment Question Generator',
              desc: 'Vani prepares a prioritized list of intelligent, clinically relevant questions to ask your oncologist based on your exact cancer stage, treatment line, and recent test results.',
            },
            {
              icon: Activity,
              title: 'Medication & Side-Effect Tracker',
              desc: 'Log chemotherapy, immunotherapy, and targeted oral meds. Get timely reminders and evidence-based relief strategies verified by board-certified oncologists.',
            },
            {
              icon: Building2,
              title: '50-State NCI Hospital & Trial Matcher',
              desc: 'Instantly search accredited NCI Comprehensive Cancer Centers across all 50 states, compare second opinion options, and discover matching Phase I-III clinical trials.',
            },
          ].map((item, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 border border-forest-ink/10 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-5">
                <item.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold font-serif text-forest-ink mb-2">{item.title}</h3>
              <p className="text-sm text-forest-ink/75 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Table: ByOnco App vs Standard Portals */}
      <section className="bg-white border-y border-forest-ink/10 py-20 px-6 sm:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
              How ByOnco Compares to Hospital Portals & Generic Apps
            </h2>
            <p className="text-forest-ink/70 text-base">
              See why ByOnco is celebrated as the best cancer app and AI oncology agent nationwide:
            </p>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-forest-ink/10 shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-forest-ink text-cream-paper text-xs uppercase tracking-wider">
                <tr>
                  <th className="py-4 px-6">App Feature</th>
                  <th className="py-4 px-6 bg-emerald-700 font-bold">ByOnco App (Vani AI)</th>
                  <th className="py-4 px-6">Hospital Portal (MyChart)</th>
                  <th className="py-4 px-6">Generic Health Apps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-forest-ink/10">
                <tr>
                  <td className="py-4 px-6 font-semibold text-forest-ink">Natural Voice Conversation</td>
                  <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Yes: Full Multi-Turn Voice AI (Vani)</td>
                  <td className="py-4 px-6 text-forest-ink/70">No (Static Inbox)</td>
                  <td className="py-4 px-6 text-forest-ink/70">No</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold text-forest-ink">Oncology-Trained AI Agent</td>
                  <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Yes: NCCN Guidelines & Peer-Reviewed Oncology</td>
                  <td className="py-4 px-6 text-forest-ink/70">No AI Assistance</td>
                  <td className="py-4 px-6 text-forest-ink/70">Generic LLM / Hallucination Risk</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold text-forest-ink">Lab & Biopsy Report Plain-English Translation</td>
                  <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Instant Explanations of Biomarkers & Stages</td>
                  <td className="py-4 px-6 text-forest-ink/70">Raw Medical Jargon Only</td>
                  <td className="py-4 px-6 text-forest-ink/70">None</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold text-forest-ink">Smart Doctor Question Generator</td>
                  <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Personalized Questions Tailored to Next Visit</td>
                  <td className="py-4 px-6 text-forest-ink/70">None</td>
                  <td className="py-4 px-6 text-forest-ink/70">Basic Generic Checklists</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold text-forest-ink">Cross-Hospital Second Opinion Matching</td>
                  <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Direct Access to 68 NCI Centers Nationwide</td>
                  <td className="py-4 px-6 text-forest-ink/70">Locked to Single Health System</td>
                  <td className="py-4 px-6 text-forest-ink/70">None</td>
                </tr>
                <tr>
                  <td className="py-4 px-6 font-semibold text-forest-ink">Red-Flag Emergency Detection</td>
                  <td className="py-4 px-6 bg-emerald-50 text-emerald-950 font-bold">Automatic Triage for Post-Chemo Fevers & Sepsis</td>
                  <td className="py-4 px-6 text-forest-ink/70">Manual Message (Takes 24-48h to Read)</td>
                  <td className="py-4 px-6 text-forest-ink/70">None</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Safety & Clinical Guardrails */}
      <section className="bg-forest-ink/5 py-20 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <span>Medically Governed & Safe</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-4">
            Engineered with Uncompromising Clinical Safety
          </h2>
          <p className="text-forest-ink/75 text-base leading-relaxed mb-8">
            Vani AI is developed under the guidance of board-certified oncologists and medical researchers. It is designed to empower, educate, and coordinate your care while never substituting for your personal treating physician's final medical diagnosis.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left text-xs">
            <div className="bg-white rounded-xl p-5 border border-forest-ink/10">
              <div className="font-bold text-forest-ink mb-1">HIPAA Compliant</div>
              <div className="text-forest-ink/70">256-bit encryption protecting all personal health data and voice interactions.</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-forest-ink/10">
              <div className="font-bold text-forest-ink mb-1">Zero Hallucination Guardrails</div>
              <div className="text-forest-ink/70">Directly grounded in NCCN clinical practice guidelines and medical literature.</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-forest-ink/10">
              <div className="font-bold text-forest-ink mb-1">Physician Escalation</div>
              <div className="text-forest-ink/70">Automated handoff prompts whenever high-risk acute symptoms are detected.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-white border-t border-forest-ink/10 py-20 px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions About the ByOnco Cancer App
            </h2>
            <p className="text-forest-ink/70 text-base">
              Common questions about using our app and conversational AI agent.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What makes ByOnco the best cancer app for patients and caregivers?',
                a: 'ByOnco integrates continuous voice conversation with Vani AI, personalized clinical question generation, pathology report decoders, and nationwide NCI cancer center matching into a single intuitive application built specifically for cancer journeys.',
              },
              {
                q: 'How does Vani, the best cancer AI agent, work?',
                a: 'Vani listens to your questions in real time using conversational voice intelligence. It provides immediate, empathetic answers aligned with NCCN guidelines, helps you track medication schedules, and summarizes complex test findings into easy-to-understand terms.',
              },
              {
                q: 'Can the ByOnco app help me find clinical trials and top oncologists?',
                a: 'Yes. The app contains a built-in search directory covering all 50 US States, allowing you to filter accredited NCI Comprehensive Cancer Centers, request virtual second opinions, and identify clinical trials tailored to your tumor biomarker profile.',
              },
              {
                q: 'Do I need to download an app from the App Store or Google Play?',
                a: 'The ByOnco app works instantly in any web browser on your phone, tablet, or desktop with zero installation required, and can be saved to your home screen as a Progressive Web App (PWA) with one click.',
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-cream-paper rounded-2xl p-6 border border-forest-ink/10">
                <h3 className="text-lg font-bold text-forest-ink mb-2">{faq.q}</h3>
                <p className="text-sm text-forest-ink/75 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 mt-16 text-center">
        <div className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-14 shadow-xl">
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-4">
            Experience the World's Best Cancer AI Companion Now
          </h2>
          <p className="text-emerald-100/90 text-base max-w-2xl mx-auto mb-8 font-sans">
            Start talking to Vani right now in your browser with zero setup. Get answers, peace of mind, and clinical clarity 24/7.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={talkUrl('/')}
              className="px-8 py-4 rounded-xl bg-emerald-400 hover:bg-emerald-300 text-forest-ink font-bold text-base transition-colors shadow-lg flex items-center justify-center gap-2"
            >
              <Mic className="w-5 h-5" /> Start Talking to Vani
            </a>
            <Link
              to="/cancer-centers-by-state"
              className="px-8 py-4 rounded-xl bg-white/15 hover:bg-white/25 text-white border border-white/20 font-semibold text-base transition-colors"
            >
              Explore 50-State Cancer Directory
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
